import { useState, useEffect } from 'react';
import { X, Plus, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

interface TriggerSelectorProps {
  selectedTriggers: string[];
  onTriggersChange: (triggers: string[]) => void;
  maxTriggers?: number;
}

// Pre-defined common triggers
const DEFAULT_TRIGGERS = [
  'Work Stress',
  'Period',
  'Travel',
  'Family Visit',
  'Social Event',
  'Alcohol',
  'Sleep Deprived',
  'Sick',
  'Financial Stress',
  'Work Deadline',
  'Argument',
  'Alone Time',
  'Quality Time',
  'Exercise',
  'No Exercise',
];

export function TriggerSelector({ 
  selectedTriggers, 
  onTriggersChange,
  maxTriggers = 5 
}: TriggerSelectorProps) {
  const [customTriggers, setCustomTriggers] = useState<string[]>([]);
  const [newTrigger, setNewTrigger] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Load custom triggers from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('tension_custom_triggers');
    if (stored) {
      setCustomTriggers(JSON.parse(stored));
    }
  }, []);

  const allTriggers = [...DEFAULT_TRIGGERS, ...customTriggers];

  const handleToggleTrigger = (trigger: string) => {
    if (selectedTriggers.includes(trigger)) {
      onTriggersChange(selectedTriggers.filter(t => t !== trigger));
    } else {
      if (selectedTriggers.length >= maxTriggers) {
        toast.error(`Maximum ${maxTriggers} triggers allowed`);
        return;
      }
      onTriggersChange([...selectedTriggers, trigger]);
    }
  };

  const handleAddCustomTrigger = () => {
    const trimmed = newTrigger.trim();
    
    if (!trimmed) {
      toast.error('Please enter a trigger name');
      return;
    }

    if (trimmed.length > 30) {
      toast.error('Trigger name too long (max 30 characters)');
      return;
    }

    if (allTriggers.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('Trigger already exists');
      return;
    }

    const updated = [...customTriggers, trimmed];
    setCustomTriggers(updated);
    localStorage.setItem('tension_custom_triggers', JSON.stringify(updated));
    
    // Auto-select the new trigger
    handleToggleTrigger(trimmed);
    
    setNewTrigger('');
    setShowCustomInput(false);
    toast.success('Custom trigger added');
  };

  const handleDeleteCustomTrigger = (trigger: string) => {
    const updated = customTriggers.filter(t => t !== trigger);
    setCustomTriggers(updated);
    localStorage.setItem('tension_custom_triggers', JSON.stringify(updated));
    
    // Remove from selected if it was selected
    if (selectedTriggers.includes(trigger)) {
      onTriggersChange(selectedTriggers.filter(t => t !== trigger));
    }
    
    toast.success('Custom trigger removed');
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-neutral-600 dark:text-neutral-400" aria-hidden="true" />
          <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Context Tags (Optional)
          </label>
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Tag today's check-in with any relevant life context. We'll show you patterns later.
        </p>
      </div>

      {/* Selected count */}
      {selectedTriggers.length > 0 && (
        <div className="text-xs text-neutral-500 dark:text-neutral-500">
          {selectedTriggers.length} / {maxTriggers} selected
        </div>
      )}

      {/* Trigger pills */}
      <div className="flex flex-wrap gap-2">
        {allTriggers.map((trigger) => {
          const isSelected = selectedTriggers.includes(trigger);
          const isCustom = customTriggers.includes(trigger);
          
          return (
            <div key={trigger} className="relative group">
              <button
                onClick={() => handleToggleTrigger(trigger)}
                className={`
                  px-3 py-1.5 rounded-full text-sm transition-all
                  ${isSelected 
                    ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 ring-1 ring-pink-300 dark:ring-pink-700' 
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                  }
                `}
                type="button"
              >
                {trigger}
              </button>
              
              {/* Delete button for custom triggers */}
              {isCustom && !isSelected && (
                <button
                  onClick={() => handleDeleteCustomTrigger(trigger)}
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  type="button"
                  aria-label={`Delete ${trigger}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add custom trigger */}
      {showCustomInput ? (
        <div className="flex gap-2">
          <Input
            value={newTrigger}
            onChange={(e) => setNewTrigger(e.target.value)}
            placeholder="Enter custom trigger"
            maxLength={30}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddCustomTrigger();
              } else if (e.key === 'Escape') {
                setShowCustomInput(false);
                setNewTrigger('');
              }
            }}
            autoFocus
            className="flex-1"
          />
          <Button
            onClick={handleAddCustomTrigger}
            variant="outline"
            size="sm"
            type="button"
          >
            Add
          </Button>
          <Button
            onClick={() => {
              setShowCustomInput(false);
              setNewTrigger('');
            }}
            variant="ghost"
            size="sm"
            type="button"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          onClick={() => setShowCustomInput(true)}
          variant="outline"
          size="sm"
          className="text-neutral-600 dark:text-neutral-400"
          type="button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Custom Trigger
        </Button>
      )}
    </div>
  );
}
